import React, { useEffect, useState } from "react";
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { Table, TableHead, TableBody, TableRow, TableCell, Typography, Paper, IconButton, Tooltip, Button, Stack, Chip } from "@mui/material";
import { Delete, Edit, Add, LinkOff } from "@mui/icons-material";
import axiosInstance from "../utils/axiosInstance";
import DeviceModal from "../components/DeviceModal";

const PickingDevicesPage = () => {
    const { showSnackbar } = useSnackbar();
    const [warehouses, setWarehouses] = useState([]);
    const [devices, setDevices] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingDevice, setEditingDevice] = useState(null);
    const [error, setError] = useState(null);

    // 一覧取得
    const fetchDevices = async () => {
        try {
            const res = await axiosInstance.get("/picking/devices");
            setDevices(res.data);
        } catch (err) {
            setError(err?.response?.data?.message || '端末一覧の取得に失敗しました');
        }
    };

    const fetchWarehouses = async () => {
        try {
            const res = await axiosInstance.get("/warehouse");
            setWarehouses(res.data);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
        }
    };

    useEffect(() => {
        fetchDevices();
        fetchWarehouses();
    }, []);

    const handleOpenAdd = () => {
        setEditingDevice({ device_name: "", warehouse_id: "", is_active: true });
        setOpen(true);
    };

    const handleOpenEdit = (device) => {
        setEditingDevice({
            ...device,
            warehouse_id: device.warehouse_id || "",
        });
        setOpen(true);
    };

    const handleDelete = async (deviceId) => {
        if (!deviceId || !window.confirm("本当に削除しますか？")) return;
        try {
            await axiosInstance.delete(`/picking/devices/${deviceId}`);
            fetchDevices();
            showSnackbar('端末を削除しました', 'success');
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
        }
    };

    const handleResetDeviceIp = async (deviceId) => {
        if (!deviceId || !window.confirm("この端末の紐づけを解除しますか？")) return;
        try {
            const res = await axiosInstance.post("/picking/devices/reset", { device_id: deviceId });
            fetchDevices();
            showSnackbar(res?.data?.message || '端末の紐づけを解除しました', 'success');
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '端末紐づけの解除に失敗しました', 'error');
        }
    };

    if (error) return <GlobalError errorMessage={error} />;
    return (
        <>
            <Stack direction="row" alignItems='center' justifyContent="space-between">
                <Typography variant="h1">ピッキング端末管理</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
                    端末追加
                </Button>
            </Stack>

            <Paper variant="section">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '40%' }}>端末名</TableCell>
                            <TableCell>倉庫</TableCell>
                            <TableCell width={120}>ステータス</TableCell>
                            <TableCell width={120}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {devices.map(d => (
                            <TableRow key={d.device_id}>
                                <TableCell>{d.device_name}</TableCell>
                                <TableCell>{d.warehouse_name || "-"}</TableCell>
                                <TableCell>
                                    <Chip label={d.is_active ? "有効" : "無効"} color={d.is_active ? "success" : "default"} size="small" />
                                </TableCell>
                                <TableCell>
                                    <Tooltip title="編集">
                                        <IconButton size="small" onClick={() => handleOpenEdit(d)}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="紐づけ解除">
                                        <span>
                                            <IconButton size="small" color="warning" onClick={() => handleResetDeviceIp(d.device_id)}>
                                                <LinkOff fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="削除">
                                        <IconButton size="small" color="error" onClick={() => handleDelete(d.device_id)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <DeviceModal
                open={open}
                onClose={() => setOpen(false)}
                defaultValues={editingDevice || { device_name: "", warehouse_id: "", is_active: true }}
                warehouses={warehouses}
                onSuccess={fetchDevices}
            />
        </>
    );
};

export default PickingDevicesPage;